import React from 'react';
import "../../styles/Documentation.css";
import { Link, Typography } from '@mui/material';
import {ImageFigure} from './utils/ContentFigure';
import CodeBlock from './utils/CodeBlock';
import fig1 from '../../images/api-docs/fig1_website_api.webp';
import fig2 from '../../images/api-docs/fig2_postman.webp';
import fig3 from '../../images/api-docs/fig3_postman_attr.webp';
import fig4 from '../../images/api-docs/fig4_getdata_powerquery.webp';
import fig5 from '../../images/api-docs/fig5_blankquerycode.webp';
import fig6 from '../../images/api-docs/fig6_powerqueryview.webp';

const SAMPLES_JSON = `
{
    "type": "samples",
    "filters": {
      "country": ["Argentina", "Canada", "Oman"],
      "site_type": "outcrop",
      "toc": [0.025, 1.5]
    },
    "show": ["country", "fe", "toc", "site_type", "section_name", "height_meters"]
}`;

const ANALYSES_JSON = `
{
    "type": "analyses",
    "filters": {
      "country": ["Argentina", "Canada", "Oman"],
      "site_type": "outcrop",
      "ana_code":["Fe","TOC"]
    },
    "show": ["height_meters","section_name","country","site_type","analysis_ref_short","analysis_doi"]
}`;

const FILTER_VALUES_JSON = `
{
  "attribute": "lithology_name"
}`;

const R_INSTALL = `
install.packages("httr2")
install.packages("jsonlite")`;

const R_SCRIPT = `
library(httr2)
library(jsonlite)

# Define the JSON query (can be pasted directly from the website)
json_query <- '{
  "type": "samples",
  "filters": {
    "country": ["Argentina", "Canada", "Oman"],
    "site_type": "outcrop",
    "toc": [0.025, 1.5]
  },
  "show": ["country", "fe", "toc", "site_type", "section_name", "height_meters"]
}'

# Submit the request to the SGP API
response <- request("https://sgp-search.io/api/v1/post") |>
  req_headers("Content-Type" = "application/json") |>
  req_body_raw(json_query, type = "application/json") |>
  req_perform()

# Check status — 200 means success
resp_status(response)

# Parse into a data frame
df <- response |>
  resp_body_string() |>
  fromJSON(flatten = TRUE)

# Preview
dim(df)
head(df)`;

const R_TYPE_CONVERT = `df <- type.convert(df, as.is = TRUE)`;

const R_MAKE_NAMES = `names(df) <- make.names(names(df), unique = TRUE)`;

const R_CLEAN_NAMES = `
library(janitor)
df <- clean_names(df)`;

const R_EXPORT = `write.csv(df, "sgp_results.csv", row.names = FALSE)`;

const PY_INSTALL = `pip install requests pandas`;

const PY_SCRIPT = `
import requests
import pandas as pd

# Define the JSON query (can be pasted directly from the website)
json_query = '''
{
  "type": "samples",
  "filters": {
    "country": ["Argentina", "Canada", "Oman"],
    "site_type": "outcrop",
    "toc": [0.025, 1.5]
  },
  "show": ["country", "fe", "toc", "site_type", "section_name", "height_meters"]
}
'''

# Submit the request
response = requests.post(
  "https://sgp-search.io/api/v1/post",
  headers={"Content-Type": "application/json"},
  data=json_query
)

# Check status — 200 means success
print(response.status_code)
response.raise_for_status()  # stops execution and raises an error if the request failed

# Parse the returned JSON into a data frame
df = pd.DataFrame(response.json())

# Preview
print(df.shape)
print(df.head())`;

const PY_TYPE_CONVERT = `df = df.apply(lambda col: pd.to_numeric(col, errors="coerce").fillna(col))`;

const PY_RENAME = `
df = df.rename(columns={
  "Fe (wt%)": "fe",
  "TOC (wt%)": "toc",
  "section name": "section_name"
})`;

const PY_REGEX = `
df.columns = (df.columns
    .str.lower()
    .str.replace(r"[^\\w]", "_", regex=True)
    .str.strip("_")
    .str.replace(r"_+", "_", regex=True)
)`;

const PY_EXPORT = `df.to_csv("sgp_results.csv", index=False)`;

const POWER_QUERY = `
let
    // API endpoint URL
    url = "https://sgp-search.io/api/v1/post",

    // Define the JSON query (pasted from website, but each single double quote " must be replaced by two "")
    body = "{
        ""type"": ""samples"",
        ""filters"": {
            ""country"": [""Argentina"", ""Canada"", ""Oman""],
            ""site_type"": ""outcrop"",
            ""toc"": [0.025, 1.5]
        },
        ""show"": [""country"", ""fe"", ""toc"", ""site_type"", ""section_name"", ""height_meters""]
    }",

    // Submit the request to the SGP API
    response = Web.Contents(url, [
        Headers = [#"Content-Type" = "application/json"],
        Content = Text.ToBinary(body)
    ]),

    // Parse the returned JSON and expand into a table
    json = Json.Document(response),
    table = Table.FromList(json, Splitter.SplitByNothing()),
    expanded = Table.ExpandRecordColumn(table, "Column1",
        Record.FieldNames(table{0}[Column1]))

in
    expanded`;

const PublicAPIDescription = () => (
    <div className="documentation-page">
        <Typography variant='h4' className="documentation-contents-header">Public API Description</Typography>
        <div className="api-description">
            <Typography variant='h5' gutterBottom>Overview</Typography>
            <Typography variant="body2" paragraph>
                The <Link href="https://sgp-search.io/search" target="_blank">SGP search page</Link> is
                the easiest way to explore the database and build queries interactively. However, users also
                have direct access to the API (Application Programming Interface), the same mechanism for
                accessing data that underlies the search interface.
            </Typography>
            <Typography variant="body2" paragraph>
                We anticipate three main use cases. First, to make a search reproducible and archivable: you
                can save the JSON query alongside your data or publication so others can exactly replicate your
                dataset. Second, to pull data directly into working environments such as R or Python. Third, to
                re-run or slightly modify a search without repeating all the steps in the user interface.
            </Typography>
            <Typography variant="body2" paragraph>
                The most straightforward workflow, therefore, is to build your initial search on the website,
                copy the generated API call (Fig.1), and then use that in e.g., R or Python to retrieve and work
                with the results directly. However, the call can also be constructed from scratch, as described
                below.
            </Typography>

            <ImageFigure
                src={fig1}
                alt="Copying the API call from the SGP search page"
                caption={<><b>Fig.1:</b> Copy the API call from the search page simply by clicking the API icon in the top right.</>}
            />

            <Typography variant="body2" paragraph>
                If you have used APIs for databases such as Macrostrat or the Paleobiology Database (PBDB), the
                process for SGP may look unfamiliar - SGP primarily uses a POST request rather than a GET
                request. Most simple database APIs embed the query directly in the URL, which works well for
                straightforward searches. SGP queries can be considerably more complex - range values, nested
                filters, multiple conditions - and POST allows these to be expressed cleanly as a structured
                JSON object rather than a URL string. The practical consequence is that for most use cases you
                cannot simply paste a query into a browser address bar to test it - instead, a tool such as
                Postman, curl, R or Python is required, as outlined below.
            </Typography>
            <Typography variant="body2" paragraph>
                Some basic count searches are available via GET requests; see the bottom of this page.
            </Typography>
            <Typography variant="body2" paragraph>
                Note that the search website is based on a static dataset (SGP Phase 2), and therefore the
                results returned from a given API call will remain the same every time the search is run.
            </Typography>

            <Typography variant='h5' gutterBottom>URL</Typography>
            <Typography variant="body2" paragraph>
                This is the <b>base URL</b> for the SGP API, which users access using the <b>HTTP POST method</b> with
                a JSON request body - step-by-step examples provided below. <i>Do not enter this URL into a web
                browser address bar</i> (browsers issue GET requests and can't send the required JSON query body).
            </Typography>
            <CodeBlock>https://sgp-search.io/api/v1/post</CodeBlock>
            <Typography variant="body2" paragraph>
                This is the base URL for lists of attribute values (only works for attributes that are available
                to filter on in the search website)
            </Typography>
            <CodeBlock>https://sgp-search.io/api/v1/post/attr</CodeBlock>
            <Typography variant='h5' gutterBottom>Understanding the API request body</Typography>

            <Typography variant='h6' gutterBottom>Primary Parameters</Typography>
            <Typography variant="body2" paragraph>
                Three main parameters can be seen in each request body: <b>type, filters and show</b>.
            </Typography>
            <Typography variant="body2" paragraph>
                <b>type:</b> defines the broad structure in which data are returned, and there are two options:
            </Typography>
            <Typography variant="body2" paragraph>
                <ul>
                    <li>
                        <b>samples</b>: returns data in a wide format, where each geochemical analyte selected
                        is an attribute (appears in its own column). Data is processed: oxide values are
                        converted, replicates are averaged and measurements are standardized to a given unit.
                        Corresponds to the 'Simple' or 'Detailed' searches in the user interface.
                    </li>
                    <li>
                        <b>analyses</b>: returns data in a long format, with values as originally published or
                        submitted. Each row represents a single analytical measurement (a sample with multiple
                        measurements will appear in multiple rows, and geochemical analytes are values of the
                        attribute "ana_code"). No data processing is applied. Corresponds to the 'Analyses'
                        search in the user interface.
                    </li>
                </ul>
            </Typography>
            <Typography variant="body2" paragraph>
                <b>filters</b>: the search options to filter the dataset. Multiple filters can be added, and all
                separate filter conditions must be met in order to return a record (applied with AND logic).
            </Typography>
            <Typography variant="body2" paragraph>
                <b>show</b>: this specifies which columns should be included in the results, as an array of
                attribute names. Attributes used in filters do not automatically appear in the results; they
                must also be listed in show. The exception is within an analyses type search - it is enough to
                specify the analytes in the filters for the measured geochemical values to appear in the results
                along with columns for unit and the analyte name.
            </Typography>

            <Typography variant='h6' gutterBottom>Search Attributes</Typography>
            <Typography variant="body2" paragraph>
                The <Link href="https://sgp-search.io/documentation/search-attributes" target="_blank">Search Attributes Dictionary</Link> lists
                all the API names for attributes that can be shown or filtered on (e.g., country, site_type,
                height_meters).
            </Typography>
            <Typography variant="body2" paragraph>
                We also recommend exploring the <Link href="https://sgp-docs.github.io/sgp_phase2/" target="_blank">database documentation</Link> and <Link href="https://github.com/ufarrell/sgp_phase2/wiki" target="_blank">wiki</Link> to
                fully understand what kind of data is available - fields are variably populated depending on data source.
            </Typography>
            <Typography variant="body2" paragraph>
                It is also important to note that the <Link href="https://sgp-search.io/documentation/search-attributes" target="_blank">Search Attributes Dictionary</Link> lists
                both the "Attribute API" and the "Attribute ID". Attribute API names are the terms used in API
                calls (e.g., height_meters), while Attribute IDs are the human-readable versions that appear in
                the search interface (e.g., height/depth). The difference between the two can be important when
                accessing data programmatically, see examples below.
            </Typography>

            <Typography variant='h6' gutterBottom>Filter values</Typography>
            <Typography variant="body2" paragraph>
                A list of valid values for any given attribute can be retrieved using this URL:
            </Typography>
            <CodeBlock>https://sgp-search.io/api/v1/post/attr</CodeBlock>
            <Typography variant="body2" paragraph>
                For example, this request body will give a list of lithology names:
            </Typography>
            <CodeBlock>{FILTER_VALUES_JSON}</CodeBlock>
            <Typography variant="body2" paragraph>
                This is most useful for well-defined categories such as country, site_type, lithology_name or
                data_source, and less useful for larger tables (e.g., strat_unit names may be easier to find in
                the search website dropdowns). Note: This only returns value lists for attributes that are
                available as filter options on the SGP search website. You can still filter on any attribute in
                the API, but the value list is not available for all of them.
            </Typography>

            <Typography variant='h6' gutterBottom>Data types</Typography>
            <Typography variant="body2" paragraph>
                In the call there are four accepted data types for the filter values :
            </Typography>
            <table className="documentation-attributes-table">
                <tr>
                    <th>Type</th>
                    <th>Example</th>
                    <th>Description</th>
                </tr>
                <tr>
                    <td>String</td>
                    <td>"outcrop"</td>
                    <td>Matches the exact string (case-sensitive; must match dictionary values exactly) i.e., only outcrops</td>
                </tr>
                <tr>
                    <td>String Array</td>
                    <td>["Argentina","Canada","Oman"]</td>
                    <td>Matches at least one of the given strings (case-sensitive; must match dictionary values exactly) i.e., Argentina or Canada or Oman</td>
                </tr>
                <tr>
                    <td>Number</td>
                    <td>1</td>
                    <td>Matches the exact value of 1</td>
                </tr>
                <tr>
                    <td>Number range</td>
                    <td>[0.025, 1.5]</td>
                    <td>Matches values between 0.025 and 1.5. Accepts null if you want to specify only a min or max value e.g., [0.025, null] for values greater or equal to 0.025</td>
                </tr>
            </table>

            <Typography variant='h6' gutterBottom style={{ marginTop: '16px' }}>Examples</Typography>
            <Typography variant="body2" paragraph>
                The following examples demonstrate the structure of the API JSON request body, for the two types
                of API call described above. Values have been chosen for illustration purposes only.
            </Typography>
            <Typography variant="body2" paragraph>1. A samples search</Typography>
            <Typography variant="body2" paragraph>Here we are looking for samples</Typography>
            <Typography variant="body2" paragraph>
                <ul>
                    <li>from Argentina, Canada or Oman</li>
                    <li>from outcrop sites only</li>
                    <li>with a total organic carbon content (toc) between 0.025 and 1.5 wt%</li>
                </ul>
            </Typography>
            <Typography variant="body2" paragraph>The results should include columns for</Typography>
            <Typography variant="body2" paragraph>
                <ul>
                    <li>country, iron, toc, site type, section name, and the height in the section.</li>
                </ul>
            </Typography>
            <Typography variant="body2" paragraph>The API request body (formatted as JSON) looks like this:</Typography>
            <CodeBlock>{SAMPLES_JSON}</CodeBlock>

            <Typography variant="body2" paragraph>2. An analyses search</Typography>
            <Typography variant="body2" paragraph>Here we are looking for the unprocessed data for a similar dataset:</Typography>
            <Typography variant="body2" paragraph>
                <ul>
                    <li>samples from Argentina, Canada or Oman</li>
                    <li>from outcrop sites only</li>
                    <li>all values for iron and total organic carbon</li>
                </ul>
            </Typography>
            <Typography variant="body2" paragraph>The results should include columns for</Typography>
            <Typography variant="body2" paragraph>
                <ul>
                    <li>
                        country, site type, section name, height in the section, short reference (e.g., Smith
                        et al. 2020), and DOI.
                    </li>
                </ul>
            </Typography>
            <Typography variant="body2" paragraph>The API request body (formatted as JSON) looks like this:</Typography>
            <CodeBlock>{ANALYSES_JSON}</CodeBlock>

            <Typography variant='h5' gutterBottom style={{ marginTop: '16px' }}>Using the API</Typography>
            <Typography variant="body2" paragraph>
                Below we present a few basic examples as a starting point, showing how the request body can be
                used in practice. Some are most useful to quickly test the API call, others for directly
                importing and using the data.
            </Typography>
            <Typography variant="body2" paragraph>
                In each case an API request body in JSON format copied from the search website can be directly
                inserted (with minor formatting required in the Power Query example).
            </Typography>

            <Typography variant='h6' gutterBottom>Postman</Typography>
            <Typography variant="body2" paragraph>
                <Link href="https://www.postman.com/home/" target="_blank">Postman</Link> is an online tool used
                mainly for building and managing APIs, but it is also a useful place to test the API call in a
                user-friendly interface.
            </Typography>
            <Typography variant="body2" paragraph>The screenshot below illustrates the key steps:</Typography>
            <Typography variant="body2" paragraph>
                <ul>
                    <li>A: Choose "Post data"</li>
                    <li>B: Insert the URL: https://sgp-search.io/api/v1/post</li>
                    <li>C: Paste API call in JSON format (can be pasted directly from the website)</li>
                    <li>D: Send</li>
                    <li>
                        E: The result is shown in JSON format (Note the code in green "200 OK" which shows a
                        successful call - see <Link href="https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Status" target="_blank">here</Link> for
                        other types of response)
                    </li>
                    <li>
                        F: The results can be downloaded in JSON format and then used elsewhere e.g., imported
                        to R, Python or Excel. However, this environment is probably most useful for quickly
                        testing API calls - all of the examples mentioned have ways of directly accessing the
                        API call internally (see below).
                    </li>
                </ul>
            </Typography>

            <ImageFigure
                src={fig2}
                alt="Postman interface"
                caption={<><b>Fig.2:</b> Postman interface.</>}
            />

            <Typography variant="body2" paragraph>
                This environment is also be useful for checking attribute values using
                https://sgp-search.io/api/v1/post/attr
            </Typography>

            <ImageFigure
                src={fig3}
                alt="Postman interface, attribute list example"
                caption={<><b>Fig.3:</b> Postman interface, attribute list example.</>}
            />

            <Typography variant='h6' gutterBottom>R</Typography>
            <Typography variant="body2" paragraph>
                There are several different approaches that can be used to access data in <Link href="https://www.r-project.org/" target="_blank">R</Link> -
                the example below was chosen because it allows for a direct copy-paste of the API call from the website.
            </Typography>
            <Typography variant="body2" paragraph>The required packages in this example are</Typography>
            <Typography variant="body2" paragraph>
                <ul>
                    <li><Link href="https://httr2.r-lib.org/" target="_blank">httr2</Link> - for making the HTTP request</li>
                    <li><Link href="https://www.rdocumentation.org/packages/jsonlite/versions/2.0.0" target="_blank">jsonlite</Link> - for parsing JSON</li>
                </ul>
            </Typography>
            <Typography variant="body2" paragraph>Install if needed:</Typography>
            <CodeBlock>{R_INSTALL}</CodeBlock>
            <Typography variant="body2" paragraph>Sample R script:</Typography>
            <CodeBlock>{R_SCRIPT}</CodeBlock>
            <Typography variant="body2" paragraph>Other useful steps to include:</Typography>
            <Typography variant="body2" paragraph>
                <b>Conversion of the datatypes</b> - the API returns all values as strings, this converts each
                column to the appropriate type automatically.
            </Typography>
            <CodeBlock>{R_TYPE_CONVERT}</CodeBlock>
            <Typography variant="body2" paragraph><b>Standardization of the column names</b></Typography>
            <Typography variant="body2" paragraph>
                At the moment, the column names returned are in the human-readable format seen on the website,
                which can be problematic for data processing. There are several options:
            </Typography>
            <Typography variant="body2" paragraph>
                1. This will replace the special characters and spaces (e.g., Fe (wt%) becomes Fe..wt..):
            </Typography>
            <CodeBlock>{R_MAKE_NAMES}</CodeBlock>
            <Typography variant="body2" paragraph>
                2. Using the <Link href="https://rdocumentation.org/packages/janitor/versions/2.2.1" target="_blank">janitor package</Link> to
                produce clean underscored names e.g., Fe (wt%) becomes fe_wt_percent:
            </Typography>
            <CodeBlock>{R_CLEAN_NAMES}</CodeBlock>
            <Typography variant="body2" paragraph><b>Export</b></Typography>
            <Typography variant="body2" paragraph>Export as CSV:</Typography>
            <CodeBlock>{R_EXPORT}</CodeBlock>

            <Typography variant='h6' gutterBottom>Python</Typography>
            <Typography variant="body2" paragraph>
                The process in <Link href="https://docs.python.org/3/" target="_blank">Python</Link> is similar to
                R. The packages needed for this example are
            </Typography>
            <Typography variant="body2" paragraph>
                <ul>
                    <li><Link href="https://pypi.org/project/requests/" target="_blank">requests</Link> — for making the HTTP request (equivalent to httr2 in R)</li>
                    <li><Link href="https://pypi.org/project/pandas/" target="_blank">pandas</Link> — for the data frame and export</li>
                </ul>
            </Typography>
            <Typography variant="body2" paragraph>Install if needed:</Typography>
            <CodeBlock>{PY_INSTALL}</CodeBlock>
            <Typography variant="body2" paragraph>Sample Python script:</Typography>
            <CodeBlock>{PY_SCRIPT}</CodeBlock>
            <Typography variant="body2" paragraph>As with R, other useful steps include:</Typography>
            <Typography variant="body2" paragraph><b>Conversion of the datatypes</b></Typography>
            <CodeBlock>{PY_TYPE_CONVERT}</CodeBlock>
            <Typography variant="body2" paragraph><b>Standardization of the column names</b></Typography>
            <Typography variant="body2" paragraph>Names can be changed manually e.g.,</Typography>
            <CodeBlock>{PY_RENAME}</CodeBlock>
            <Typography variant="body2" paragraph>
                Or with regex - this will convert to lowercase, replace non-word special characters with
                underscores, and then clean up double underscores, such that e.g., Fe (wt%) becomes fe_wt.
            </Typography>
            <CodeBlock>{PY_REGEX}</CodeBlock>
            <Typography variant="body2" paragraph><b>Export</b></Typography>
            <Typography variant="body2" paragraph>Export as CSV:</Typography>
            <CodeBlock>{PY_EXPORT}</CodeBlock>

            <Typography variant='h6' gutterBottom>Excel/Power Query</Typography>
            <Typography variant="body2" paragraph>
                <Link href="https://learn.microsoft.com/en-us/power-query/" target="_blank">Power Query</Link> can
                be used to load results directly into Excel. The following example was done using a Mac, steps
                may differ slightly on Windows.
            </Typography>
            <Typography variant="body2" paragraph>1. Open Power Query</Typography>
            <Typography variant="body2" paragraph>Data → Get Data (Power Query) → Blank Query.</Typography>

            <ImageFigure
                src={fig4}
                alt="Excel (for Mac) Get Data (Power Query)"
            />

            <Typography variant="body2" paragraph>2. Paste the query code.</Typography>
            <Typography variant="body2" paragraph>An example is given below. If asked, set access to Anonymous.</Typography>
            <Typography variant="body2" paragraph>
                <b>Note the double quotes</b> e.g., ""country"" instead of "country". This is due to the
                combination of the Power Query language AND JSON, both of which require double quotes. If you are
                pasting the API call from the website into this code, a find-and-replace of " with "" is needed.
            </Typography>
            <CodeBlock>{POWER_QUERY}</CodeBlock>

            <ImageFigure src={fig5} alt="Blank query code in Power Query" />

            <Typography variant="body2" paragraph>3. Check the results and import to excel</Typography>
            <Typography variant="body2" paragraph>
                Check and modify as necessary. Click "Close & load" to bring the data into an Excel table.
            </Typography>

            <ImageFigure src={fig6} alt="Power Query view of the imported data" />

            <Typography variant='h5' gutterBottom>GET requests</Typography>
            <Typography variant="body2" paragraph>
                Some simple counts are available via GET (these can be directly pasted into a browser):
            </Typography>
            <Typography variant="body2" paragraph>
                <ul>
                    <li>count of results: <Link href="https://sgp-search.io/api/v1/get/info/results" target="_blank">https://sgp-search.io/api/v1/get/info/results</Link></li>
                    <li>count of samples: <Link href="https://sgp-search.io/api/v1/get/info/samples" target="_blank">https://sgp-search.io/api/v1/get/info/samples</Link></li>
                    <li>count of countries: <Link href="https://sgp-search.io/api/v1/get/info/countries" target="_blank">https://sgp-search.io/api/v1/get/info/countries</Link></li>
                </ul>
            </Typography>
        </div>
    </div>
);

export default PublicAPIDescription;
